/**
 * AutoReplyDialog — Per-company auto-reply configuration for Social-Inbox.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, MessageCircle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_ACTIONS = [
  { key: "answer_compliments", label: "Komplimente beantworten", desc: "z.B. 'Wow, wie schoen' → 'Danke vielmals!'" },
  { key: "answer_faq", label: "Haeufige Fragen", desc: "z.B. Oeffnungszeiten, Standort, Dienstleistungen" },
  { key: "forward_inquiries", label: "Anfragen weiterleiten", desc: "Komplexe Anfragen an Mensch eskalieren" },
  { key: "confirm_appointments", label: "Termine bestaetigen", desc: "Standard-Bestaetigungen — NICHT neu terminieren" },
  { key: "thank_followers", label: "Followern danken", desc: "Kurzes Dankeschoen bei neuen Followern" },
];

export default function AutoReplyDialog({ companyId, companyName, open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    axios.get(`${API}/companies/${companyId}/auto-reply`, { withCredentials: true })
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, [open, companyId]);

  const toggleAction = (k) => {
    setCfg((c) => {
      const set = new Set(c.allowed_actions || []);
      if (set.has(k)) set.delete(k); else set.add(k);
      return { ...c, allowed_actions: [...set] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${companyId}/auto-reply`, cfg, { withCredentials: true });
      toast.success("Auto-Reply-Konfiguration gespeichert");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="autoreply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#2563EB]" /> Auto-Reply fuer {companyName}
          </DialogTitle>
        </DialogHeader>

        {loading || !cfg ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--studio-text-muted)]" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Master toggle */}
            <div className="p-3 rounded-lg border border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/5 to-transparent">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-medium">KI-Auto-Reply aktivieren</p>
                  <p className="text-[10px] text-[var(--studio-text-muted)]">
                    Die KI liest neue DMs/Kommentare und generiert Antwort-Vorschlaege nach deinen Regeln.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!cfg.enabled}
                  onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))}
                  className="w-4 h-4 accent-[#2563EB]"
                  data-testid="autoreply-enabled"
                />
              </label>
            </div>

            {/* Require approval */}
            {cfg.enabled && (
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-[var(--studio-border)]">
                <input
                  type="checkbox"
                  checked={!!cfg.require_approval}
                  onChange={(e) => setCfg((c) => ({ ...c, require_approval: e.target.checked }))}
                  className="w-4 h-4 mt-0.5 accent-[#2563EB]"
                  data-testid="autoreply-require-approval"
                />
                <div className="flex-1">
                  <p className="text-xs font-medium">Menschliche Freigabe vor dem Senden erforderlich</p>
                  <p className="text-[10px] text-[var(--studio-text-muted)]">
                    Empfohlen: Ja. Die KI generiert nur Vorschlaege — ein Mensch klickt "Senden".
                    Wenn deaktiviert: KI postet bei Confidence &gt;= 75% automatisch (nur Komplimente/FAQs).
                  </p>
                </div>
              </label>
            )}

            {/* Tone */}
            <div>
              <Label className="text-xs">Tonalitaet der Antworten</Label>
              <Input
                value={cfg.tone || ""}
                onChange={(e) => setCfg((c) => ({ ...c, tone: e.target.value }))}
                placeholder="freundlich, professionell"
                className="mt-1"
                data-testid="autoreply-tone"
              />
            </div>

            {/* Allowed actions */}
            <div>
              <Label className="text-xs">Was darf die KI machen?</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {ALLOWED_ACTIONS.map((a) => {
                  const active = (cfg.allowed_actions || []).includes(a.key);
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => toggleAction(a.key)}
                      className={`p-2 rounded-lg border text-left transition-all ${
                        active
                          ? "border-[#2563EB] bg-[#2563EB]/5 ring-1 ring-[#2563EB]/30"
                          : "border-[var(--studio-border)] hover:bg-[var(--studio-bg)]"
                      }`}
                      data-testid={`action-${a.key}`}
                    >
                      <p className="text-xs font-medium">{a.label}</p>
                      <p className="text-[10px] text-[var(--studio-text-muted)]">{a.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Forbidden */}
            <div>
              <Label className="text-xs">Was die KI NICHT darf (Verbote)</Label>
              <Textarea
                value={cfg.forbidden_topics || ""}
                onChange={(e) => setCfg((c) => ({ ...c, forbidden_topics: e.target.value }))}
                rows={2}
                placeholder="Keine verbindlichen Preise. Keine rechtlichen Auskuenfte. Keine medizinischen Ratschlaege."
                className="mt-1 text-xs"
                data-testid="autoreply-forbidden"
              />
            </div>

            {/* Escalate keywords */}
            <div>
              <Label className="text-xs">Eskalations-Keywords (komma-separiert)</Label>
              <Textarea
                value={cfg.escalate_keywords || ""}
                onChange={(e) => setCfg((c) => ({ ...c, escalate_keywords: e.target.value }))}
                rows={2}
                placeholder="Preis, Kosten, Vertrag, Termin, Beschwerde, Rueckgabe"
                className="mt-1 text-xs"
                data-testid="autoreply-escalate"
              />
              <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                Enthaelt eine Nachricht eines dieser Worte → KI setzt Status auf "Eskaliert" und antwortet NICHT automatisch.
              </p>
            </div>

            {/* Forwarding / Webhook */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Weiterleiten per E-Mail an</Label>
                <Input
                  type="email"
                  value={cfg.forward_email || ""}
                  onChange={(e) => setCfg((c) => ({ ...c, forward_email: e.target.value }))}
                  placeholder="support@firma.ch"
                  className="mt-1 text-xs"
                  data-testid="autoreply-email"
                />
                <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                  Eskalierte Nachrichten landen in diesem Postfach (benoetigt SMTP-Setup).
                </p>
              </div>
              <div>
                <Label className="text-xs">Webhook-URL fuer Daten-Export</Label>
                <Input
                  value={cfg.webhook_url || ""}
                  onChange={(e) => setCfg((c) => ({ ...c, webhook_url: e.target.value }))}
                  placeholder="https://n8n.firma.ch/webhook/inbox"
                  className="mt-1 text-xs font-mono"
                  data-testid="autoreply-webhook"
                />
                <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                  Jede neue Inbox-Nachricht wird per POST an diese URL gesendet (n8n / Zapier / CRM).
                </p>
              </div>
            </div>

            <div className="p-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Die KI beachtet automatisch die Firmen-Daten (Branche, Services, Tonalitaet, Zielgruppe),
                die du im Firmen-Profil eingetragen hast. Du musst hier nichts doppelt eintragen.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="autoreply-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={save}
            disabled={saving || !cfg}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="autoreply-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
